import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUpdateInvoiceMutation } from '@/api'
import { ArrowUpDownIcon, EllipsisVerticalIcon } from 'lucide-react'
import {
	Badge,
	Button,
	DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
	IconButton,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@rentflowdev/ui'
import {
	CURRENCY_SIGNS, DEFAULT_LOCALE,
	INVOICE_STATUSES, INVOICE_STATUS_KEYS, INVOICE_STATUS_VARIANTS,
	INVOICE_TYPES, INVOICE_TYPE_KEYS,
} from '@/types'
import type { Invoice, InvoiceStatus, InvoiceType } from '@/types'
import { useListInvoicesQuery } from '@/api'
import { InvoiceDetailsDialog } from '@/components/invoice-details-dialog'

type SortField = 'title' | 'amount' | 'period'
type SortDir = 'asc' | 'desc'

function formatCurrency(amount: number, currency: string): string {
	const sign = CURRENCY_SIGNS[currency] ?? currency
	return `${amount.toLocaleString(DEFAULT_LOCALE)}${sign}`
}

function formatDate(iso: string): string {
	return new Date(iso).toLocaleDateString(DEFAULT_LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' })
}

interface RentalInvoicesSectionProps {
	rentalId: string
}

export function RentalInvoicesSection({ rentalId }: RentalInvoicesSectionProps) {
	const { t } = useTranslation()
	const [typeFilter, setTypeFilter] = useState<InvoiceType | ''>('')
	const [statusFilter, setStatusFilter] = useState<InvoiceStatus | ''>('')
	const [sortField, setSortField] = useState<SortField>('period')
	const [sortDir, setSortDir] = useState<SortDir>('desc')
	const [selected, setSelected] = useState<Invoice | null>(null)

	const { data } = useListInvoicesQuery({ rentalId })
	const invoices: Invoice[] = data?.invoices ?? []

	const filtered = useMemo(() => {
		return invoices
			.filter((i) => !typeFilter || i.type === typeFilter)
			.filter((i) => !statusFilter || i.status === statusFilter)
	}, [invoices, typeFilter, statusFilter])

	const sorted = useMemo(() => {
		const arr = [...filtered]
		const dir = sortDir === 'asc' ? 1 : -1
		arr.sort((a, b) => {
			switch (sortField) {
				case 'title':
					return a.title.localeCompare(b.title) * dir
				case 'amount':
					return (Number(a.amount) - Number(b.amount)) * dir
				case 'period':
					return (new Date(a.periodStart).getTime() - new Date(b.periodStart).getTime()) * dir
			}
		})
		return arr
	}, [filtered, sortField, sortDir])

	function toggleSort(field: SortField) {
		if (sortField === field) {
			setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
		} else {
			setSortField(field)
			setSortDir('asc')
		}
	}

	return (
		<div className="flex flex-col gap-3">
			<div className="flex flex-wrap items-center gap-2">
				<Select
					value={typeFilter || undefined}
					onValueChange={(v) => setTypeFilter(v as InvoiceType)}
				>
					<SelectTrigger width="md" tone="muted">
						<SelectValue placeholder={t('rent.invoices.filters.type')} />
					</SelectTrigger>
					<SelectContent>
						{INVOICE_TYPES.map((typ) => (
							<SelectItem key={typ} value={typ}>{t(INVOICE_TYPE_KEYS[typ])}</SelectItem>
						))}
					</SelectContent>
				</Select>
				<Select disabled>
					<SelectTrigger width="md" tone="muted">
						<SelectValue placeholder={t('rent.invoices.filters.period')} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">{t('rent.filters.allPeriods')}</SelectItem>
					</SelectContent>
				</Select>
				<Select
					value={statusFilter || undefined}
					onValueChange={(v) => setStatusFilter(v as InvoiceStatus)}
				>
					<SelectTrigger width="md" tone="muted">
						<SelectValue placeholder={t('rent.invoices.filters.status')} />
					</SelectTrigger>
					<SelectContent>
						{INVOICE_STATUSES.map((s) => (
							<SelectItem key={s} value={s}>{t(INVOICE_STATUS_KEYS[s])}</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			<div className="overflow-hidden rounded-(--radius-md) border border-(--color-border)">
				<Table>
					<TableHeader>
						<TableRow className="hover:bg-transparent">
							<TableHead>
								<Button
									variant="ghost"
									iconRight={<ArrowUpDownIcon className="size-3.5" />}
									onClick={() => toggleSort('title')}
									className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
								>
									{t('rent.invoices.columns.title')}
								</Button>
							</TableHead>
							<TableHead className="w-[180px] font-[500] text-(--color-muted-foreground)">
								{t('rent.invoices.columns.period')}
							</TableHead>
							<TableHead className="w-[120px]">
								<Button
									variant="ghost"
									iconRight={<ArrowUpDownIcon className="size-3.5" />}
									onClick={() => toggleSort('amount')}
									className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
								>
									{t('rent.invoices.columns.amount')}
								</Button>
							</TableHead>
							<TableHead className="w-[140px] font-[500] text-(--color-muted-foreground)">
								{t('rent.invoices.columns.status')}
							</TableHead>
							<TableHead className="w-[56px]" />
						</TableRow>
					</TableHeader>
					<TableBody>
						{sorted.map((inv) => (
							<InvoiceRow
								key={inv.id}
								invoice={inv}
								t={t}
								onOpen={() => setSelected(inv)}
							/>
						))}
					</TableBody>
				</Table>
			</div>

			<InvoiceDetailsDialog
				open={selected !== null}
				onOpenChange={(next) => { if (!next) setSelected(null) }}
				invoice={selected}
			/>
		</div>
	)
}

function InvoiceRow({ invoice, t, onOpen }: { invoice: Invoice; t: (k: string) => string; onOpen: () => void }) {
	const [updateInvoice] = useUpdateInvoiceMutation()

	return (
		<TableRow className="h-16 cursor-pointer" onClick={onOpen}>
			<TableCell className="px-4">
				<div className="flex flex-col gap-0.5">
					<span className="text-sm font-[500] text-(--color-foreground)">
						{invoice.title}
					</span>
					<span className="text-xs leading-4 text-(--color-muted-foreground)">
						{formatDate(invoice.createdAt)}
					</span>
				</div>
			</TableCell>
			<TableCell className="px-4 text-sm text-(--color-foreground) whitespace-nowrap">
				{formatDate(invoice.periodStart)} -<br />{formatDate(invoice.periodEnd)}
			</TableCell>
			<TableCell className="px-4 text-sm text-(--color-foreground)">
				{formatCurrency(Number(invoice.amount), invoice.currency)}
			</TableCell>
			<TableCell className="px-4">
				<Badge variant={INVOICE_STATUS_VARIANTS[invoice.status]} size="sm">
					{t(INVOICE_STATUS_KEYS[invoice.status])}
				</Badge>
			</TableCell>
			<TableCell className="px-4" onClick={(e) => e.stopPropagation()}>
				<DropdownMenu>
					<DropdownMenuTrigger render={<IconButton variant="ghost" size="sm" aria-label={t('rent.invoices.actions.menu')} />}>
						<EllipsisVerticalIcon className="size-4" />
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end" className="min-w-40">
						<DropdownMenuItem onClick={onOpen}>{t('rent.invoices.actions.view')}</DropdownMenuItem>
						<DropdownMenuItem onClick={() => void updateInvoice({ id: invoice.id, body: { status: 'CANCELLED' } })}>
							{t('rent.invoices.actions.cancel')}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</TableCell>
		</TableRow>
	)
}
